/**
 * Entity-neutral text search primitives.
 *
 * Normalization, tokenizing and matching live here so member search and
 * SubKrewe search share one notion of what "matches" means. Entity-specific
 * field sets layer on top: see memberSearch.ts and subkreweSearch.ts.
 */

/** Shortest query worth running. Below this, pickers show nothing. */
export const SEARCH_MIN_LENGTH = 2

/** Default result cap for pickers and autocompletes. */
export const SEARCH_DEFAULT_LIMIT = 20

/**
 * Fold a string to something comparable: accents stripped, case dropped,
 * whitespace collapsed. A krewe roster is full of accented names and nobody
 * types the accents, so `renee` has to find `Renée`.
 */
export function normalizeSearchText(value: string | null | undefined): string {
  if (!value) return ''
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/** Digits only, so `(504) 555-1234` is findable as `5045551234`. */
export function digitsOnly(value: string | null | undefined): string {
  return value ? value.replace(/\D/g, '') : ''
}

/**
 * Join already-normalized fields into the blob a query is tested against.
 * Newline-separated so a token can never match across a field boundary.
 */
export function joinSearchFields(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join('\n')
}

/**
 * Split a query into tokens that are matched with AND. Every implementation
 * this replaced tested the whole query as one substring, so `wouter bie` found
 * nobody named `Wouter de Bie`.
 */
export function tokenizeSearchQuery(query: string): string[] {
  const normalized = normalizeSearchText(query)
  return normalized ? normalized.split(' ').filter(Boolean) : []
}

/** True when every token appears somewhere in the blob. */
export function matchesSearchQuery(searchText: string, tokens: string[]): boolean {
  if (tokens.length === 0) return false

  return tokens.every((token) => {
    if (searchText.includes(token)) return true

    // A token with no letters may be a punctuated phone fragment: `555-1234`
    // should reach the digits-only copy of the number.
    if (/[a-z]/.test(token)) return false
    const digits = digitsOnly(token)
    return digits.length > 0 && searchText.includes(digits)
  })
}

/** True when a query is long enough to be worth running. */
export function isSearchableQuery(
  query: string,
  minLength: number = SEARCH_MIN_LENGTH,
): boolean {
  return normalizeSearchText(query).length >= minLength
}
